import { useEffect, useMemo, useState } from "react";
import "./AdminCoachOccupancyModule.scss";
import { toast } from "react-toastify";
import { CoachStatisticsExport } from "../../../types/api";
import StatisticsFilterList from "../StatisticsFilterList/StatisticsFilterList";

import { createStatisticsFilterStore } from "../Stores/statisticsFilterStoreFactory";
import { ReportTableSection } from "../Tables/ReportTableSection";

import { CoachChartSingle } from "../Charts/CoachChartSingle";
import { CoachChartAllMemberTypes } from "../Charts/CoachChartAllMembertypes";
import { CoachChartAllCoaches } from "../Charts/CoachChartAllCoaches";

interface AdminCoachOccupancyModuleProps {
  coachOccupancyData: CoachStatisticsExport;
  coachOccupancyFilterStore: ReturnType<typeof createStatisticsFilterStore>;
  startDate: string;
  endDate: string;
  selectedWeekdays: number[];
}

const AdminCoachOccupancyModule = ({
  coachOccupancyData,
  coachOccupancyFilterStore,
  startDate,
  endDate,
  selectedWeekdays,
}: AdminCoachOccupancyModuleProps) => {
  const [isFolded, setIsFolded] = useState<boolean>(false);
  function countWeekdaysFromIntervals(startDate: Date, endDate: Date) {
    const counts = {
      monday: 0,
      tuesday: 0,
      wednesday: 0,
      thursday: 0,
      friday: 0,
      saturday: 0,
      sunday: 0,
    };

    const start = new Date(
      Date.UTC(
        startDate.getUTCFullYear(),
        startDate.getUTCMonth(),
        startDate.getUTCDate()
      )
    );
    const end = new Date(
      Date.UTC(
        endDate.getUTCFullYear(),
        endDate.getUTCMonth(),
        endDate.getUTCDate()
      )
    );

    for (let d = new Date(start); d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
      const dayOfWeek = d.getUTCDay(); // 0 = Sunday
      const isoDay = dayOfWeek;

      if (selectedWeekdays.includes(isoDay)) {
        switch (isoDay) {
          case 1:
            counts.monday++;
            break;
          case 2:
            counts.tuesday++;
            break;
          case 3:
            counts.wednesday++;
            break;
          case 4:
            counts.thursday++;
            break;
          case 5:
            counts.friday++;
            break;
          case 6:
            counts.saturday++;
            break;
          case 0:
            counts.sunday++;
            break;
        }
      }
    }

    return counts;
  }

  const [weekdayCounts, setWeekdayCounts] = useState({
    monday: 0,
    tuesday: 0,
    wednesday: 0,
    thursday: 0,
    friday: 0,
    saturday: 0,
    sunday: 0,
  });

  const totalDays = Object.values(weekdayCounts).reduce(
    (acc, count) => acc + count,
    0
  );
  useEffect(() => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const counts = countWeekdaysFromIntervals(start, end);
    setWeekdayCounts(counts);
  }, [startDate, endDate, selectedWeekdays]);
  const [chartType, setChartType] = useState<"line" | "pie">("line");

  return (
    <div className="admin-coach-occupancy-module">
      <StatisticsFilterList
        useStore={coachOccupancyFilterStore}
        useTimeintervals={true}
        usePermissions={true}
        useCoaches={true}
      />
      <ReportTableSection
        title="Beläggning - Tränare"
        children={
          <div className="specific-member-header-buttons">
            <button
              className="select-style"
              onClick={() => setIsFolded(!isFolded)}
            >
              <i
                className="fa-solid fa-caret-down"
                style={{
                  transition: "transform 0.2s ease",
                  transform: isFolded ? "rotate(-180deg)" : "rotate(0deg)",
                }}
              />
            </button>
          </div>
        }
        columns={[
          { key: "coachName", label: "Namn" },
          { key: "timespan", label: "Tidsperiod", align: "left" },
          {
            key: "monday",
            label: `Måndag (${weekdayCounts.monday})`,
            align: "left",
          },
          {
            key: "tuesday",
            label: `Tisdag (${weekdayCounts.tuesday})`,
            align: "left",
          },
          {
            key: "wednesday",
            label: `Onsdag (${weekdayCounts.wednesday})`,
            align: "left",
          },
          {
            key: "thursday",
            label: `Torsdag (${weekdayCounts.thursday})`,
            align: "left",
          },
          {
            key: "friday",
            label: `Fredag (${weekdayCounts.friday})`,
            align: "left",
          },
          {
            key: "saturday",
            label: `Lördag (${weekdayCounts.saturday})`,
            align: "left",
          },
          {
            key: "sunday",
            label: `Söndag (${weekdayCounts.sunday})`,
            align: "left",
          },
          {
            key: "total",
            label: `Total (${totalDays})`,
            align: "left",
          },
        ]}
        rows={
          coachOccupancyData &&
          coachOccupancyData.coaches.flatMap((coach) =>
            coach.rows.map((row) => ({ ...row, coachName: coach.coachName }))
          )
        }
        useCalcType={false}
        useToggleType={false}
        onToggleType={(isPie: boolean) => setChartType(isPie ? "pie" : "line")}
        useStore={coachOccupancyFilterStore}
        isLoading={false}
        folded={isFolded}
      />

      <div className="coach-statistics-charts-container">
        {coachOccupancyData && (
          <CoachChartAllCoaches
            coaches={coachOccupancyData.coaches.filter(
              (coach) => coach.coachId !== "ALL"
            )}
            startDate={startDate}
            endDate={endDate}
          />
        )}
        {coachOccupancyData && (
          <CoachChartAllMemberTypes
            rows={coachOccupancyData.coaches
              .filter((coach) => coach.coachId !== "ALL")
              .flatMap((coach) =>
                coach.rows
                  .filter((r) => r.type === "row")
                  .map((r) => ({
                    ...r,
                    coachName: coach.coachName,
                    coachId: coach.coachId,
                  }))
              )}
            startDate={startDate}
            endDate={endDate}
          />
        )}
        {/* {coachOccupancyData &&
          coachOccupancyData.coaches.map((coachData, index) => (
            <CoachChartSingle
              key={index}
              coachName={coachData.coachName}
              rows={coachData.rows}
            />
          ))} */}
      </div>
      <div className="occupancy-spacer"></div>
    </div>
  );
};
export default AdminCoachOccupancyModule;
